import { ChevronLeftIcon, ChevronRightIcon } from "@heroicons/react/24/outline";
import type { EmblaCarouselType } from "embla-carousel";
import AutoScroll, { AutoScrollOptionsType } from "embla-carousel-auto-scroll";
import useEmblaCarousel from "embla-carousel-react";
import { memo, PropsWithChildren, useCallback, useEffect, useState } from "react";
import { classNames } from "@/helpers/tailwind-class";

function EmblaCarouselBase({
  children,
  prevButtonClass = "",
  nextButtonClass = "",
  wrapperClass = "",
  loop = false,
  autoScroll = undefined,
  scrollToItemIndex = undefined,
}: PropsWithChildren<{
  prevButtonClass?: string;
  nextButtonClass?: string;
  wrapperClass?: string;
  loop?: boolean;
  autoScroll?: AutoScrollOptionsType;
  scrollToItemIndex?: number;
}>) {
  let plugins = [];
  if (autoScroll) {
    plugins.push(AutoScroll(autoScroll));
  }

  const [emblaRef, emblaApi] = useEmblaCarousel({ loop: loop, slidesToScroll: "auto" }, plugins);
  const [prevBtnDisabled, setPrevBtnDisabled] = useState(true);
  const [nextBtnDisabled, setNextBtnDisabled] = useState(true);

  const scrollPrev = useCallback(() => {
    if (emblaApi) emblaApi.scrollPrev();
  }, [emblaApi]);

  const scrollNext = useCallback(() => {
    if (emblaApi) emblaApi.scrollNext();
  }, [emblaApi]);

  const onSelect = useCallback((emblaApi: EmblaCarouselType) => {
    setPrevBtnDisabled(!emblaApi.canScrollPrev());
    setNextBtnDisabled(!emblaApi.canScrollNext());
  }, []);

  useEffect(() => {
    if (!emblaApi) return;

    onSelect(emblaApi);
    emblaApi.on("reInit", onSelect);
    emblaApi.on("select", onSelect);
  }, [emblaApi, onSelect]);

  useEffect(() => {
    if (scrollToItemIndex === undefined) return;
    if (!emblaApi) return;
    const { slideRegistry } = emblaApi.internalEngine();
    const snapIndexThatSlideBelongsTo = slideRegistry.findIndex((group) =>
      group.includes(scrollToItemIndex)
    );
    emblaApi.scrollTo(snapIndexThatSlideBelongsTo);
  }, [emblaApi, scrollToItemIndex]);

  return (
    <div className={classNames("relative w-full", wrapperClass)}>
      <div className="overflow-hidden" ref={emblaRef}>
        {children}
      </div>

      <button
        className={classNames(
          "btn btn-circle absolute top-0 bottom-4 -left-6 my-auto flex items-center justify-center",
          prevBtnDisabled ? "hidden" : "block",
          prevButtonClass
        )}
        onClick={scrollPrev}
        disabled={prevBtnDisabled}
        role="button"
        aria-label="Previous Button"
        type="button"
      >
        <ChevronLeftIcon className="h-6 w-6" />
      </button>
      <button
        className={classNames(
          "btn btn-circle absolute top-0 -right-6 bottom-4 my-auto flex items-center justify-center",
          nextBtnDisabled ? "hidden" : "block",
          nextButtonClass
        )}
        onClick={scrollNext}
        disabled={nextBtnDisabled}
        role="button"
        aria-label="Next Button"
        type="button"
      >
        <ChevronRightIcon className="h-6 w-6" />
      </button>
    </div>
  );
}

export const EmblaCarousel = memo(EmblaCarouselBase);
